import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowLeft, Receipt, ArrowDown, Handshake, ArrowUp, SealCheck, DownloadSimple, Coins, Bank, ArrowsClockwise, ArrowCounterClockwise } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';
import CertificateModal, { downloadCertificate } from '@/components/CertificateModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { INR: '\u20B9', USD: '$', EUR: '\u20AC', GBP: '\u00A3' };

const TXN_ICONS = {
  received_payment: { icon: ArrowDown, color: 'text-green-500', bg: 'bg-green-500/10', label: 'Received (Payout)' },
  payable_cleared: { icon: ArrowUp, color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'Payable Cleared' },
  receivable_cleared: { icon: Handshake, color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Receivable Cleared' },
};

export default function TransactionHistory() {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const symbol = CURRENCY_SYMBOLS[user?.currency] || '\u20B9';

  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loans, setLoans] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCert, setActiveCert] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, txnRes, certRes, loanRes, refRes] = await Promise.all([
        axios.get(`${API}/transactions/summary`, { withCredentials: true }),
        axios.get(`${API}/transactions`, { withCredentials: true }),
        axios.get(`${API}/certificates`, { withCredentials: true }),
        axios.get(`${API}/loans`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/refunds`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSummary(sumRes.data);
      setTransactions(txnRes.data);
      setCertificates(certRes.data);
      setLoans(loanRes.data || []);
      setRefunds(refRes.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-white'}`}><div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full" /></div>;
  }

  const s = summary || {};
  const txns = transactions?.cycle_transactions || [];
  const fees = transactions?.fees || [];

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-white'}`} data-testid="transaction-history">
      <div className={`sticky top-0 z-40 backdrop-blur-xl border-b ${isDark ? 'bg-zinc-950/80 border-zinc-800' : 'bg-white/80 border-zinc-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className={`p-1.5 rounded-sm ${isDark ? 'hover:bg-zinc-800 text-zinc-400' : 'hover:bg-zinc-100 text-zinc-600'}`} data-testid="txn-back-btn"><ArrowLeft size={20} weight="bold" /></button>
          <div className="flex items-center gap-2">
            <Receipt size={20} weight="bold" className="text-yellow-400" />
            <h1 className={`font-outfit text-lg font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Transaction History</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="txn-balance-card">
            <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Balance</p>
            <p className={`font-outfit text-2xl font-bold mt-1 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{Math.round(s.current_balance || 0).toLocaleString()}</p>
            <p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>withdrawable</p>
          </div>
          <div className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="txn-received-card">
            <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Received</p>
            <p className="font-outfit text-2xl font-bold mt-1 text-green-500">{symbol}{(s.total_received || 0).toLocaleString()}</p>
            <p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>payouts</p>
          </div>
          <div className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="txn-cleared-card">
            <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Debt Cleared</p>
            <p className="font-outfit text-2xl font-bold mt-1 text-blue-400">{symbol}{(s.total_cleared || 0).toLocaleString()}</p>
            <p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>via netting</p>
          </div>
          <div className={`p-4 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid="txn-fees-card">
            <p className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Platform Fees</p>
            <p className="font-outfit text-2xl font-bold mt-1 text-yellow-400">{symbol}{(s.total_fees_paid || 0).toLocaleString()}</p>
            <p className={`font-ibm text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>paid</p>
          </div>
        </div>

        {/* Certificates */}
        <div>
          <h3 className={`font-outfit text-base font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}><SealCheck size={18} weight="bold" className="text-yellow-400" /> Certificates (Payment &amp; Discharge)</h3>
          {certificates.length === 0 ? (
            <p className={`text-center py-6 font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>No certificates yet. They appear when a cycle clears your payables/receivables.</p>
          ) : (
            <div className="space-y-2">
              {certificates.map(c => {
                const isPayment = c.cert_type === 'payment';
                const isLoan = c.cert_type === 'loan_closure';
                const badge = isLoan ? { t: 'Loan Closure', cls: 'bg-yellow-400/10 text-yellow-400' } : isPayment ? { t: 'Payment', cls: 'bg-blue-500/10 text-blue-400' } : { t: 'Discharge', cls: 'bg-green-500/10 text-green-500' };
                return (
                <div key={c.certificate_id} className={`flex items-center justify-between p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`cert-${c.certificate_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-400/10 rounded-sm flex items-center justify-center"><SealCheck size={16} className="text-yellow-400" /></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-ibm text-[10px] px-1.5 py-0.5 rounded-sm ${badge.cls}`}>{badge.t}</span>
                        <p className={`font-ibm text-sm font-medium ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(c.total_cleared || 0).toLocaleString()}</p>
                      </div>
                      <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{isLoan ? 'Loan closed with' : isPayment ? 'Paid to' : 'Received from'} {(c.cleared_debts || []).map(d => d.counterparty).join(', ')} · {new Date(c.issued_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setActiveCert(c)} className={`px-3 py-1.5 rounded-sm font-ibm text-[10px] font-semibold uppercase tracking-wider border ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-zinc-200 text-zinc-700'}`} data-testid={`view-cert-${c.certificate_id}`}>View</button>
                    <button onClick={() => downloadCertificate(c)} className="px-3 py-1.5 rounded-sm font-ibm text-[10px] font-semibold uppercase tracking-wider bg-yellow-400 text-zinc-950 flex items-center gap-1" data-testid={`dl-cert-${c.certificate_id}`}><DownloadSimple size={12} weight="bold" /> PDF</button>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div>

        {/* Loans Cleared */}
        {loans.filter(l => ['settling', 'closed'].includes(l.status)).length > 0 && (
          <div data-testid="loans-cleared-section">
            <h3 className={`font-outfit text-base font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}><Bank size={18} weight="bold" className="text-yellow-400" /> Loans</h3>
            <div className="space-y-2">
              {loans.filter(l => ['settling', 'closed'].includes(l.status)).map(l => {
                const closed = l.status === 'closed';
                const cert = certificates.find(c => c.cert_type === 'loan_closure' && c.loan_id === l.loan_id);
                return (
                  <div key={l.loan_id} className={`flex items-center justify-between p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`txn-loan-${l.loan_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${closed ? 'bg-green-500/10' : 'bg-yellow-400/10'}`}>
                        {closed ? <SealCheck size={16} className="text-green-500" /> : <ArrowsClockwise size={16} className="text-yellow-400 animate-spin" style={{ animationDuration: '2s' }} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`font-ibm text-[10px] px-1.5 py-0.5 rounded-sm ${closed ? 'bg-green-500/10 text-green-500' : 'bg-yellow-400/10 text-yellow-400'}`}>{closed ? 'Loan Paid' : 'Payment In Process'}</span>
                          <p className={`font-ibm text-sm font-medium ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(l.loan_amount || 0).toLocaleString()}</p>
                        </div>
                        <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{l.institution} · A/C {l.account_number}{closed && l.closure ? ` · ref ${l.closure.reference}` : ''}</p>
                      </div>
                    </div>
                    {closed && cert && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setActiveCert(cert)} className={`px-3 py-1.5 rounded-sm font-ibm text-[10px] font-semibold uppercase tracking-wider border ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-zinc-200 text-zinc-700'}`} data-testid={`view-loan-cert-${l.loan_id}`}>View</button>
                        <button onClick={() => downloadCertificate(cert)} className="px-3 py-1.5 rounded-sm font-ibm text-[10px] font-semibold uppercase tracking-wider bg-yellow-400 text-zinc-950 flex items-center gap-1" data-testid={`dl-loan-cert-${l.loan_id}`}><DownloadSimple size={12} weight="bold" /> PDF</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Refunds */}
        {refunds.length > 0 && (
          <div data-testid="refunds-section">
            <h3 className={`font-outfit text-base font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}><ArrowCounterClockwise size={18} weight="bold" className="text-yellow-400" /> Refunds</h3>
            <div className="space-y-2">
              {refunds.map(r => {
                const done = r.status === 'completed';
                return (
                  <div key={r.refund_id} className={`flex items-center justify-between p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`refund-${r.refund_id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${done ? 'bg-green-500/10' : 'bg-blue-500/10'}`}><ArrowCounterClockwise size={16} className={done ? 'text-green-500' : 'text-blue-400'} /></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`font-ibm text-[10px] px-1.5 py-0.5 rounded-sm ${done ? 'bg-green-500/10 text-green-500' : 'bg-blue-500/10 text-blue-400'}`}>{done ? 'Refunded' : 'In Process'}</span>
                          <p className={`font-ibm text-sm font-medium ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{symbol}{(r.amount || 0).toLocaleString()}</p>
                        </div>
                        <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{r.reason}{done && r.completion ? ` · ref ${r.completion.reference}` : (r.eligible_at ? ` · eligible ${new Date(r.eligible_at).toLocaleDateString()}` : '')}</p>
                      </div>
                    </div>
                    <span className={`font-outfit text-sm font-bold ${done ? 'text-green-500' : 'text-blue-400'}`}>{symbol}{(r.amount || 0).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Cycle transactions — tabbed by direction */}
        <div>
          <h3 className={`font-outfit text-base font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Cycle Activity</h3>
          <Tabs defaultValue="payable">
            <TabsList className={`mb-4 ${isDark ? 'bg-zinc-900' : 'bg-zinc-100'}`}>
              <TabsTrigger value="payable" className="font-ibm text-xs gap-1" data-testid="tab-payable"><ArrowUp size={14} /> Payable Cleared</TabsTrigger>
              <TabsTrigger value="receivable" className="font-ibm text-xs gap-1" data-testid="tab-receivable"><Handshake size={14} /> Receivable Cleared</TabsTrigger>
              <TabsTrigger value="received" className="font-ibm text-xs gap-1" data-testid="tab-received"><ArrowDown size={14} /> Received</TabsTrigger>
            </TabsList>
            {[['payable', 'payable_cleared'], ['receivable', 'receivable_cleared'], ['received', 'received_payment']].map(([val, type]) => {
              const list = txns.filter(t => t.type === type);
              return (
                <TabsContent key={val} value={val}>
                  <div className="space-y-1.5">
                    {list.length === 0 ? (
                      <p className={`text-center py-8 font-ibm text-sm ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Nothing here yet.</p>
                    ) : list.map(t => {
                      const cfg = TXN_ICONS[t.type] || { icon: Receipt, color: 'text-zinc-400', bg: 'bg-zinc-500/10', label: t.type };
                      const Icon = cfg.icon;
                      return (
                        <motion.div key={t.transaction_id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                          className={`flex items-center justify-between p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900/50' : 'border-zinc-200 bg-zinc-50'}`} data-testid={`txn-${t.transaction_id}`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${cfg.bg}`}><Icon size={16} className={cfg.color} /></div>
                            <div>
                              <p className={`font-ibm text-sm font-medium ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{cfg.label}</p>
                              <p className={`font-ibm text-[10px] max-w-md truncate ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{t.description}</p>
                              <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>{new Date(t.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                          <span className={`font-outfit text-sm font-bold ${cfg.color}`}>{symbol}{Math.abs(t.amount).toLocaleString()}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>

        {/* Platform fee payments */}
        {fees.length > 0 && (
          <div>
            <h3 className={`font-outfit text-base font-semibold mb-3 ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>Platform Fee Payments</h3>
            <div className="space-y-2">
              {fees.map((f, i) => (
                <div key={i} className={`flex items-center justify-between p-3 border rounded-md ${isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-400/10 rounded-sm flex items-center justify-center"><Coins size={16} className="text-yellow-400" /></div>
                    <div>
                      <p className={`font-ibm text-sm font-medium ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>{f.description || 'Platform fee'}</p>
                      <p className={`font-ibm text-[10px] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>{new Date(f.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <span className="font-outfit text-sm font-bold text-yellow-400">{symbol}{(f.amount || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CertificateModal cert={activeCert} open={!!activeCert} onClose={() => setActiveCert(null)} />
    </div>
  );
}
